/**
 * Lonvik scroll engine.
 *
 * 1. IntersectionObserver reveals for `.lv-reveal` (one-shot fade/rise).
 * 2. Scroll-scrub: every element with [data-scrub] gets a `--p` custom
 *    property (0..1) each frame. CSS turns that progress into transforms,
 *    opacity ramps and parallax — one rAF loop, transform/opacity only.
 *
 *    Modes: data-scrub="exit"  progress of scrolling PAST a top-anchored
 *                              element (hero exit),
 *           data-scrub="pin"   progress through a tall sticky wrapper
 *                              (pinned scenes),
 *           data-scrub="view"  progress of travelling through the viewport
 *                              (parallax media).
 *    data-steps="n" additionally quantises progress into data-step="0..n-1"
 *    so CSS transitions can crossfade discrete beats.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Reveals ---- */
  var reveals = document.querySelectorAll('.lv-reveal');
  if (!('IntersectionObserver' in window)) {
    reveals.forEach(function (el) { el.classList.add('is-in'); });
  } else if (reveals.length) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.style.transitionDelay = (entry.target.dataset.delay || '0') + 'ms';
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    reveals.forEach(function (el) { io.observe(el); });
  }

  /* ---- Count-up: [data-count] animates its first number 0 → target when
     it enters the viewport (final text is already in the markup, so no-JS
     and reduced-motion users simply see the real value). ---- */
  var counters = document.querySelectorAll('[data-count]');
  if (counters.length && 'IntersectionObserver' in window && !reduced) {
    var cio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        cio.unobserve(entry.target);
        var el = entry.target;
        var final = el.textContent;
        var m = final.match(/[\d]+(?:[.,]\d+)?/);
        if (!m) return;
        var target = parseFloat(m[0].replace(',', '.'));
        var prefix = final.slice(0, m.index);
        var suffix = final.slice(m.index + m[0].length);
        var dur = 1300;
        var t0 = null;
        var tick = function (ts) {
          if (t0 === null) t0 = ts;
          var k = Math.min(1, (ts - t0) / dur);
          var e = 1 - Math.pow(1 - k, 3); /* easeOutCubic */
          if (k < 1) {
            el.textContent = prefix + Math.round(target * e) + suffix;
            requestAnimationFrame(tick);
          } else {
            el.textContent = final;
          }
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.6 });
    counters.forEach(function (el) { cio.observe(el); });
  }

  /* ---- Scrub engine ---- */
  if (reduced) return;

  var scenes = Array.prototype.slice.call(document.querySelectorAll('[data-scrub]'));
  if (!scenes.length) return;

  /* Frame-scrubbed videos: a video[data-scrub-frames] inside a [data-scrub]
     scene gets its timeline driven by the same progress the engine writes
     (Apple-style scroll-locked playback — seeking only, never autoplay).
     Registered only when the cinema layout is actually active (the CSS
     hides the field in the static mobile/reduced layouts, and preload=none
     in the markup keeps those contexts from downloading the file). */
  scenes.forEach(function (el) {
    var v = el.querySelector('video[data-scrub-frames]');
    if (!v) return;
    /* the field is display:none in the static layouts — a child's own
       computed display never reflects that, so test layout presence
       instead (offsetParent is null inside a display:none subtree) */
    if (!v.offsetParent) return;
    /* narrow viewports get the lightweight encode when one is declared */
    var msrc = v.getAttribute('data-mobile-src');
    if (msrc && window.innerWidth <= 820) {
      var src = v.querySelector('source');
      if (src) { src.setAttribute('src', msrc); } else { v.setAttribute('src', msrc); }
    }
    el._scrubVideo = v;
    v.preload = 'auto';
    v.load();
  });

  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  var header = document.querySelector('.lv-header');
  var ticking = false;

  /* Hand-off between pinned scenes: a [data-snap-next="#id"] runway carries
     the reader into the next band once its pin has played out, so the long
     scrub ends on arrival instead of on a dead stretch of runway.

     Rules that keep it from feeling like a trap: it only ever fires
     downward, only from a pin (the mobile/static layouts read progress
     differently), only once per pass — it re-arms only after the reader has
     come back up past 0.9 — and never under prefers-reduced-motion, which is
     precisely a request not to be moved without asking. The reader can
     interrupt it at any point: any scroll input cancels a smooth scroll. */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var lastY = window.scrollY;
  var scrollDir = 1;

  function snapNext(el, sel, p) {
    if (p < 0.985) {
      /* back on the runway: arm it again for the next pass down */
      if (p < 0.9) el._snapped = false;
      return;
    }
    if (el._snapped || scrollDir < 0 || reduceMotion.matches) return;
    var target = document.querySelector(sel);
    if (!target) return;
    var top = window.scrollY + target.getBoundingClientRect().top;
    /* already there (or past it) — nothing to hand off */
    if (top - window.scrollY < 8) return;
    el._snapped = true;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function update() {
    ticking = false;
    var vh = window.innerHeight;
    var vw = window.innerWidth;
    var y = window.scrollY;
    if (y !== lastY) { scrollDir = y > lastY ? 1 : -1; lastY = y; }
    /* Page reading progress for the header bar */
    if (header) {
      var max = document.documentElement.scrollHeight - vh;
      header.style.setProperty('--sp', max > 0 ? (window.scrollY / max).toFixed(4) : '0');
    }
    for (var i = 0; i < scenes.length; i++) {
      var el = scenes[i];
      var r = el.getBoundingClientRect();
      /* Far offscreen: progress is pinned at 0 or 1 anyway — skip the write */
      if (r.bottom < -vh || r.top > vh * 2) continue;

      var mode = el.getAttribute('data-scrub');
      /* narrow-viewport override (e.g. the pathway scene: pinned on desktop,
         a simple viewport pass on mobile so the frame-locked video sweeps
         the whole section instead of saturating early) */
      if (vw <= 820) {
        var mm = el.getAttribute('data-scrub-mobile');
        if (mm) mode = mm;
      }
      var p;
      if (mode === 'exit') {
        p = clamp01(-r.top / (r.height * 0.85));
      } else if (mode === 'pin') {
        p = clamp01(-r.top / Math.max(1, r.height - vh));
      } else {
        p = clamp01((vh - r.top) / (vh + r.height));
      }
      el.style.setProperty('--p', p.toFixed(4));

      var snapSel = el.getAttribute('data-snap-next');
      if (snapSel && mode === 'pin') snapNext(el, snapSel, p);

      /* frame-locked background video: map progress onto the timeline
         (skip seeks below ~1 frame at 24fps — seeking is decode work) */
      var fv = el._scrubVideo;
      if (fv && fv.duration) {
        var t = p * fv.duration;
        if (Math.abs(fv.currentTime - t) > 0.035) {
          try { fv.currentTime = t; } catch (err) {}
        }
      }

      var steps = el.getAttribute('data-steps');
      if (steps) {
        var n = parseInt(steps, 10);
        var s = Math.min(n - 1, Math.floor(p * n));
        if (el.getAttribute('data-step') !== String(s)) {
          el.setAttribute('data-step', String(s));
        }
      }
    }
  }

  function queue() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue, { passive: true });
  update();
})();
